import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, Input, IntlPhoneInput, Select } from '@insify/design-system/components';
import { textSm } from '@insify/design-system/typography';
import { RichContent, WizardLayoutFormWithCommonSection, useWizard } from '@insify/funnel-components';
import { validatePhoneE164 } from '@insify/global-validation';
import { emailRegex, shuffle } from '@insify/ui-utils';
import { TrackedAnchor } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useCommands } from '../../hooks/useCommands';
import { useParamsQuery } from '../../hooks/useQueries';

import styles from './Contact.module.css';

import type { Commands } from '../../models/commands';
import type { TFunction } from 'i18next';

const validation = (t: TFunction<['validation' | 'common' | 'funnel']>) => {
  return yup.object().shape({
    contact: yup.object().shape({
      email: yup.string().required(t('validation:email_required')).matches(emailRegex, t('validation:email_invalid')),
      phone: yup
        .string()
        .required(t('validation:phone_required'))
        .test('phone', t('validation:phone_invalid'), validatePhoneE164),
      termsAndConditionsConsent: yup.bool().required().oneOf([true]),
    }),
  });
};

const howDidYouHearAboutUsOptions: string[] = [
  'LinkedIn',
  'Instagram',
  'Site comparatif',
  'Reddit',
  'Facebook',
  'Recommandé par un proche',
  'Google',
  'ChatGPT',
];

const shuffledHowDidYouHearAboutUsOptions: string[] = [...shuffle(howDidYouHearAboutUsOptions), 'Autre'];

export const ContactPage: React.FC = () => {
  const { t } = useTranslation(['funnel', 'common', 'validation']);
  const { showPreviousPage, showNextPage } = useWizard();
  const { sendMultipleCommands } = useCommands();
  const { data: quoteParams, isStale: paramsStale } = useParamsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);

  const { handleSubmit, register, formState, control } = useForm({
    resolver: yupResolver(validation(t)),
    shouldUnregister: true,
    defaultValues: {
      contact: {
        email: quoteParams?.representative?.email ?? '',
        phone: quoteParams?.representative?.phone ?? '',
        howDidYouHearAboutUs: quoteParams?.howDidYouHearAboutUs ?? '',
        termsAndConditionsConsent: quoteParams?.termsAndConditionsConsent ?? false,
      },
    },
    mode: 'onSubmit',
  });

  const hasCompletedContactStep = Boolean(
    triggerShowingNextPage && !paramsStale && quoteParams?.representative?.email && quoteParams.representative?.phone,
  );
  useEffect(() => {
    if (hasCompletedContactStep) {
      showNextPage();
      setTriggerShowingNextPage(false);
    }
  }, [hasCompletedContactStep]);

  const submit = async ({
    contact: { email, phone, howDidYouHearAboutUs },
  }: {
    contact: { email: string; phone: string; howDidYouHearAboutUs: string };
  }) => {
    setIsSubmitting(true);

    const commands: Commands[] = [
      {
        type: 'enterPhoneNumber',
        payload: {
          phoneNumber: phone,
        },
      },
      {
        type: 'enterEmail',
        payload: {
          email,
        },
      },
      {
        type: 'giveTermsAndConditionsConsent',
        payload: {
          consent: true,
        },
      },
    ];

    if (howDidYouHearAboutUs)
      commands.push({
        type: 'enterHowDidYouHearAboutUs',
        payload: {
          howDidYouHearAboutUs,
        },
      });

    await sendMultipleCommands(commands);

    setTriggerShowingNextPage(true);
  };

  return (
    <WizardLayoutFormWithCommonSection
      backCopy={t('common:back')}
      onBack={showPreviousPage}
      forwardCopy={t('common:continue')}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(submit)}
    >
      <div className={styles.introCopy}>
        <RichContent.H2>{t('funnel:contact_form_title')}</RichContent.H2>
        <RichContent.P className={styles.paragraph}>{t('funnel:contact_form_subtitle')}</RichContent.P>
      </div>
      <Input
        type='email'
        id='email'
        trackingId='email'
        error={formState.errors.contact?.email?.message}
        {...register('contact.email')}
        autoComplete='email'
        placeholder={t('funnel:contact_form_email_placeholder')}
        disabled={isSubmitting}
        label={t('funnel:contact_form_email_label')}
      />
      <Controller
        name='contact.phone'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <IntlPhoneInput
            id='phone'
            trackingId='phone'
            value={field.value}
            onChange={field.onChange}
            ref={field.ref}
            error={error?.message}
            placeholder={t('funnel:contact_form_phone_placeholder')}
            label={t('funnel:contact_form_phone_label')}
            defaultCountry='fr'
            preferredCountries={['fr', 'nl', 'be', 'de', 'pl', 'gb', 'hu', 'ro']}
            disabled={isSubmitting}
          />
        )}
      />
      <Select
        {...register('contact.howDidYouHearAboutUs')}
        label={t('funnel:contact_form_how_did_you_hear_about_us_label')}
        topHint={t('funnel:contact_form_how_did_you_hear_about_us_hint')}
        placeholder={t('funnel:contact_form_how_did_you_hear_about_us_placeholder')}
        required={true}
        disabled={isSubmitting}
      >
        {shuffledHowDidYouHearAboutUsOptions.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </Select>
      <div className={styles.consentBlock}>
        <div className={styles.checkBox}>
          <Checkbox
            trackingId='consent'
            {...register('contact.termsAndConditionsConsent')}
            className={styles.checkBox}
            id='termsAndConditionsConsent'
            error={formState.errors.contact?.termsAndConditionsConsent?.message}
            data-testid='termsAndConditionsConsent'
          />
        </div>
        <label htmlFor='termsAndConditionsConsent' className={clsx(textSm, styles.consentLabel)}>
          <Trans
            i18nKey={'funnel:contact_form_terms_and_conditions'}
            components={{
              privacy: (
                <TrackedAnchor
                  target='_blank'
                  rel='noreferrer'
                  objectiv={{ id: 'privacy-policy' }}
                  href='https://www.insify.fr/politique-de-confidentialite/'
                />
              ),
              terms: (
                <TrackedAnchor
                  target='_blank'
                  rel='noreferrer'
                  objectiv={{ id: 'terms-and-conditions' }}
                  href='https://www.insify.fr/cgu/'
                />
              ),
            }}
          />
        </label>
      </div>
    </WizardLayoutFormWithCommonSection>
  );
};
